import React, { useState, useEffect, useCallback } from "react";
import { Card, Row, Col, Spin, message, Button, Empty, Popconfirm, Switch, Modal, DatePicker } from "antd";
import { useParams, useHistory } from "react-router-dom";
import { EditOutlined, ReloadOutlined, DeleteOutlined, LineChartOutlined } from "@ant-design/icons";

const { RangePicker } = DatePicker;
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ReferenceArea,
} from "recharts";
import Dashboard from "../api/dashboard";
import { OilMonitor, Gas } from "../api";
import InfraredCamera from "../api/InfraredCamera";
import CustomHeader from "../container/Header";
import PageDirector from "../container/PageDirector";
import { useGlobal } from "../global/user";
import { GasChartComponent, OilChartComponent, SimpleChartComponent, WaterChartComponent, Function15ChartComponent } from "../components/DashboardCharts";
import MediaCard from "../components/MediaCard";
import { LivePositionTracker } from "../container/modal/LiveDashboardModal/components/LivePositionTracker";
import bgSpaceBlue from '../image/space-blue.jpeg';

const DashboardView = () => {
  const { companyId } = useParams();
  const history = useHistory();
  const { user } = useGlobal();
  
  const [loading, setLoading] = useState(true);
  const [dashboardConfig, setDashboardConfig] = useState(null);
  const [paramValues, setParamValues] = useState({});
  const [chartData, setChartData] = useState({});
  const [machinesData, setMachinesData] = useState({}); // 存儲機具的完整資訊（按 equipment_id 索引）
  const [onlineStatus, setOnlineStatus] = useState({}); // 在線狀態
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [hoveredBtn, setHoveredBtn] = useState(null);
  const [resetZoomHovered, setResetZoomHovered] = useState(false);
  
  // 高級圖表 Modal 狀態
  const [advancedChartVisible, setAdvancedChartVisible] = useState(false);
  const [selectedChartConfig, setSelectedChartConfig] = useState(null);
  
  // 縮放功能 state
  const [zoomState, setZoomState] = useState({
    left: 'dataMin',
    right: 'dataMax',
    refAreaLeft: '',
    refAreaRight: '',
  });
  
  // 油壓圖表補點開關狀態（每個圖表一個）
  const [oilChartMakeUp, setOilChartMakeUp] = useState({});
  
  // 日期範圍選擇器狀態（用於詳細圖表 Modal）
  const [dateRange, setDateRange] = useState(null);
  
  // Infrared 圖表時間選擇狀態
  const [infraredChartRange, setInfraredChartRange] = useState('72h');
  const [infraredCustomRange, setInfraredCustomRange] = useState(null);
  const [infraredChartLoading, setInfraredChartLoading] = useState(false);

  // 載入儀表板配置
  useEffect(() => {
    loadDashboardConfig();
  }, [companyId]);

  const loadDashboardConfig = async () => {
    setLoading(true);
    try {
      const config = await Dashboard.GetByCompanyId(companyId);
      setDashboardConfig(config);
      
      if (config) {
        // 使用新 API 載入機具資料
        await loadMachinesData(config);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        message.warning("尚未建立儀表板配置");
      } else {
        message.error("載入儀表板失敗");
        console.error("載入儀表板錯誤:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  // ✨ 使用新 API 載入機具資料
  const loadMachinesData = async (config) => {
    console.log("📊 儀表板配置:", config);
    
    const allItems = [
      ...(config.parameters || []),
      ...(config.charts || []),
    ];

    // 提取所有唯一的 field_id
    const fieldIds = [...new Set(
      allItems
        .map(item => item.site_id)
        .filter(id => id)
    )];

    if (fieldIds.length === 0) {
      console.warn("⚠️ 配置中沒有 site_id，無法載入機具資料");
      return;
    }

    console.log("需要載入的場域:", fieldIds);

    const machines = {};

    try {
      // 對每個場域調用新的聚合 API
      const fieldDataPromises = fieldIds.map(async (fieldId) => {
        try {
          const response = await Dashboard.GetFieldMobilesFunctions(fieldId);
          console.log(`✅ 場域 ${fieldId} (${response.field_name}) 回應:`, response);
          return response.mobiles || [];
        } catch (error) {
          console.error(`❌ 獲取場域 ${fieldId} 的資料失敗:`, error);
          return [];
        }
      });

      const allFieldData = await Promise.all(fieldDataPromises);
      const allMobiles = allFieldData.flat(); // 合併所有場域的機具

      console.log(`從新 API 獲取了 ${allMobiles.length} 台機具`);

      // 建立 equipment_id → 機具資料的映射
      allMobiles.forEach((mobile) => {
        machines[mobile.mobile_id] = {
          id: mobile.mobile_id,
          name: mobile.mobile_name,
          mobileType_id: mobile.mobileType_id,
          IP: mobile.IP,
          port: mobile.port,
          functions: mobile.functions, // 包含所有 function 資料
        };
        
        console.log(`✅ 載入機具 ${mobile.mobile_id} (${mobile.mobile_name}), functions:`, Object.keys(mobile.functions));
      });

    } catch (error) {
      console.error("載入機具資料時發生錯誤:", error);
    }

    setMachinesData(machines);
    console.log(`最終載入了 ${Object.keys(machines).length} 台機具`);
  };

  // 🔍 判斷設備的圖表類型（根據 functions 決定使用哪個高級圖表）
  const getChartType = (equipmentId) => {
    const machine = machinesData[equipmentId];
    if (!machine || !machine.functions) {
      console.log('⚠️ [getChartType] 找不到機具或 functions:', equipmentId, machine);
      return null;
    }

    const functionKeys = Object.keys(machine.functions);
    console.log('🔍 [getChartType] equipmentId:', equipmentId, 'functions:', functionKeys);
    
    // Function 14 (MQTT 氣體監測) → GasChart
    if (functionKeys.includes('function_14')) {
      console.log('✅ [getChartType] 檢測到 function_14 (氣體監測)');
      return { 
        type: 'gas', 
        component: 'GasChart',
        functionData: machine.functions.function_14 
      };
    }
    
    // Function 7/10 (數位油壓) → OilMonitorChart
    if (functionKeys.includes('function_7') || functionKeys.includes('function_10')) {
      const funcKey = functionKeys.includes('function_7') ? 'function_7' : 'function_10';
      console.log('✅ [getChartType] 檢測到', funcKey, '(油壓監測)');
      return { 
        type: 'oil', 
        component: 'OilMonitorChart',
        functionData: machine.functions[funcKey]
      };
    }
    
    // Function 11 (水情監測) → WaterLineChart
    if (functionKeys.includes('function_11')) {
      console.log('✅ [getChartType] 檢測到 function_11 (水情監測)');
      return { 
        type: 'water', 
        component: 'WaterLineChart',
        functionData: machine.functions.function_11
      };
    }
    
    // Function 15 (紅外線熱像監測) → Function15Chart
    if (functionKeys.includes('function_15')) {
      console.log('✅ [getChartType] 檢測到 function_15 (紅外線熱像監測)');
      return { 
        type: 'infrared', 
        component: 'Function15Chart',
        functionData: machine.functions.function_15
      };
    }

    console.log('⚠️ [getChartType] 無匹配的圖表類型');
    return null;
  };

  // 打開高級圖表
  const openAdvancedChart = (chart) => {
    let chartType = getChartType(chart.equipment_id);
    
    // 如果 getChartType 找不到，嘗試從已載入的 chartData 中獲取類型
    // （用於支援 fallback 機制載入的數據）
    if (!chartType && chartData[chart.slot_number]) {
      const chartDataItem = chartData[chart.slot_number];
      if (chartDataItem.type && chartDataItem.type !== 'error') {
        console.log('✅ [openAdvancedChart] 使用 chartData 中的類型:', chartDataItem.type);
        chartType = {
          type: chartDataItem.type,
          component: chartDataItem.type === 'infrared' ? 'Function15Chart' : 'SimpleChart',
          functionData: chartDataItem.functionData || {},
        };
      }
    }
    
    if (!chartType) {
      message.warning('此設備不支援詳細圖表功能');
      return;
    }

    // 重置缩放状态和日期范围
    setZoomState({
      left: 'dataMin',
      right: 'dataMax',
      refAreaLeft: '',
      refAreaRight: '',
    });
    setDateRange(null);
    
    // 重置 infrared 圖表的時間選擇狀態
    if (chartType?.type === 'infrared') {
      setInfraredChartRange('72h');
      setInfraredCustomRange(null);
    }

    setSelectedChartConfig({
      ...chart,
      chartType,
      machine: machinesData[chart.equipment_id],
    });
    setAdvancedChartVisible(true);
  };

  // Infrared 圖表快捷時間範圍切換
  const handleInfraredChangeRange = async (range) => {
    if (!selectedChartConfig) return;
    const { equipment_id, slot_number } = selectedChartConfig;
    
    setInfraredChartRange(range);
    setInfraredCustomRange(null);
    setInfraredChartLoading(true);
    
    try {
      const response = await InfraredCamera.GetChartData(equipment_id, { range });
      if (response.code === 200 && response.data) {
        setChartData(prev => ({
          ...prev,
          [slot_number]: {
            ...prev[slot_number],
            data: response.data,
          }
        }));
      }
    } catch (error) {
      console.error('🔥 [Infrared] 切換時間範圍失敗:', error);
      message.error('載入圖表資料失敗');
    } finally {
      setInfraredChartLoading(false);
    }
  };
  
  // Infrared 圖表自訂時間範圍
  const handleInfraredCustomRange = async (dates) => {
    if (!selectedChartConfig) return;
    const { equipment_id, slot_number } = selectedChartConfig;
    
    if (!dates) {
      setInfraredCustomRange(null);
      await handleInfraredChangeRange('72h');
      return;
    }
    
    setInfraredCustomRange(dates);
    setInfraredChartRange(null);
    setInfraredChartLoading(true);
    
    try {
      const response = await InfraredCamera.GetChartData(equipment_id, {
        start: dates[0].toISOString(),
        end: dates[1].toISOString()
      });
      if (response.code === 200 && response.data) {
        setChartData(prev => ({
          ...prev,
          [slot_number]: {
            ...prev[slot_number],
            data: response.data,
          }
        }));
      }
    } catch (error) {
      console.error('🔥 [Infrared] 自訂時間範圍失敗:', error);
      message.error('載入圖表資料失敗');
    } finally {
      setInfraredChartLoading(false);
    }
  };

  // 根據日期範圍刷新詳細圖表數據（僅用於 Modal）
  const refreshChartByDateRange = async (startDate, endDate) => {
    if (!selectedChartConfig) return;
    
    const { equipment_id, slot_number, chartType } = selectedChartConfig;
    
    try {
      message.loading('載入數據中...', 0);
      
      // 格式化日期為後端格式
      const formatDateTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };
      
      const startDateStr = formatDateTime(startDate);
      const endDateStr = formatDateTime(endDate);
      
      console.log('📅 刷新圖表數據:', startDateStr, '到', endDateStr);
      
      // 根據圖表類型獲取數據
      if (chartType?.type === 'water') {
        const parameters = ['water_level', 'average_velocity', 'discharge'];
        const dataPromises = parameters.map(param => 
          Dashboard.GetMachineHistory(equipment_id, param, 500, startDateStr, endDateStr)
        );
        
        const results = await Promise.all(dataPromises);
        
        // 合併數據
        const timeMap = new Map();
        results.forEach((paramData, index) => {
          const paramKey = parameters[index];
          paramData.forEach(item => {
            const time = new Date(item.time).getTime();
            if (!timeMap.has(time)) {
              timeMap.set(time, { time });
            }
            timeMap.get(time)[paramKey] = item.value;
          });
        });
        
        const processedData = Array.from(timeMap.values())
          .filter(item => 
            item.water_level !== undefined && 
            item.average_velocity !== undefined && 
            item.discharge !== undefined
          )
          .sort((a, b) => a.time - b.time);
        
        setChartData(prev => ({
          ...prev,
          [slot_number]: {
            ...prev[slot_number],
            data: processedData,
          }
        }));
        
        // 重置縮放
        setZoomState({
          left: 'dataMin',
          right: 'dataMax',
          refAreaLeft: '',
          refAreaRight: '',
        });
        
        message.destroy();
        message.success(`已載入 ${processedData.length} 筆數據`);
      }
      // 其他圖表類型可在此擴展
      else {
        message.destroy();
        message.warning('此圖表類型暫不支援日期範圍選擇');
      }
      
    } catch (error) {
      message.destroy();
      message.error('載入數據失敗');
      console.error('刷新圖表數據錯誤:', error);
    }
  };

  // 處理日期範圍變更
  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
      refreshChartByDateRange(dates[0].toDate(), dates[1].toDate());
    } else {
      setDateRange(null);
    }
  };

  // ✨ 從 functions 物件中提取參數值
  const extractParameterValue = (functions, parameterKey) => {
    if (!functions || typeof functions !== 'object') {
      return null;
    }

    // 遍歷所有 function (function_2, function_5, function_6, function_7, function_11, function_14 等)
    for (const [funcName, funcData] of Object.entries(functions)) {
      if (!funcData) continue;

      // ✨ 特殊處理 function_14 (MQTT 機具)
      if (funcName === 'function_14' && funcData.sensors) {
        // parameterKey 是 'a', 'b', 'c' 等字母
        const sensor = funcData.sensors[parameterKey];
        if (sensor && sensor.value !== undefined && sensor.value !== null) {
          console.log(`✅ 從 function_14.sensors.${parameterKey} 取得值: ${sensor.value} (${sensor.name})`);
          return sensor.value; // 已經是校正後的值
        }
        continue;
      }

      // ✨ 處理 function_5 (天車控制) - 特殊狀態處理
      if (funcName === 'function_5' && parameterKey === 'status') {
        const statusValue = funcData.status;
        const statusText = {
          '-1': '離線',
          '0': '關閉',
          '1': '開啟'
        }[statusValue] || '未知';
        
        console.log(`✅ function_5 狀態: ${statusValue} (${statusText})`);
        return statusValue;
      }

      // ✨ 處理其他 function (function_2, function_4, function_6, function_7, function_11 等)
      if (funcData[parameterKey] !== undefined && funcData[parameterKey] !== null) {
        let value = funcData[parameterKey];
        const originalValue = value; // ✅ 保存原始值用於日誌
        
        // 如果有校正係數，應用校正公式
        const calibrationMap = {
          // function_2 (索道)
          voltage: { a: 'a', b: 'b' },
          resistance: { a: 'a', b: 'b' },
          current: { a: 'a', b: 'b' },
          
          // function_7 (數位油壓)
          Rpressure: { a: 'ra', b: 'rb' },
          Spressure: { a: 'sa', b: 'sb' },
          Tpressure: { a: 'ta', b: 'tb' },
          
          // function_11 (水情影像)
          water_level: { a: 'a', b: 'b' },
          average_velocity: { a: 'ra', b: 'rb' },
          discharge: { a: 'sa', b: 'sb' },
        };

        const calibration = calibrationMap[parameterKey];
        if (calibration && funcData[calibration.a] !== undefined && funcData[calibration.b] !== undefined) {
          const a = funcData[calibration.a] || 1;
          const b = funcData[calibration.b] || 0;
          value = value * a + b;
          console.log(`✅ 校正計算 (${funcName}): ${parameterKey} = ${originalValue} × ${a} + ${b} = ${value.toFixed(2)}`);
        } else {
          console.log(`✅ 直接取值 (${funcName}): ${parameterKey} = ${value}`);
        }
        
        return Math.round(value * 100) / 100; // 保留兩位小數
      }
    }

    console.warn(`⚠️ 找不到參數: ${parameterKey}`);
    return null;
  };


  // 刷新參數值
  const refreshParamValues = useCallback(async () => {
    if (!dashboardConfig?.parameters || Object.keys(machinesData).length === 0) {
      return;
    }

    const values = {};
    const status = {};

    dashboardConfig.parameters.forEach((param) => {
      const { slot_number, equipment_id, parameter_key } = param;
      
      // 跳過空的參數槽
      if (!equipment_id || !parameter_key) {
        return;
      }

      const machine = machinesData[equipment_id];
      
      if (!machine) {
        console.warn(`找不到機具 ${equipment_id} 的資訊`);
        status[equipment_id] = false;
        return;
      }

      try {
        // 從 functions 中提取參數值
        const value = extractParameterValue(machine.functions, parameter_key);
        
        if (value !== undefined && value !== null) {
          values[slot_number] = value;
          status[equipment_id] = true;
          console.log(`✅ 參數槽 ${slot_number}: ${parameter_key} = ${value}`);
        } else {
          status[equipment_id] = false;
          console.warn(`⚠️ 參數槽 ${slot_number}: 找不到 ${parameter_key}`);
        }
      } catch (error) {
        console.error(`獲取參數 ${parameter_key} 失敗:`, error);
        status[equipment_id] = false;
      }
    });

    setParamValues(values);
    setOnlineStatus(status);
  }, [dashboardConfig, machinesData]);

  // 刷新圖表數據
  const refreshChartData = useCallback(async () => {
    if (!dashboardConfig?.charts || Object.keys(machinesData).length === 0) {
      return;
    }

    const data = {};

    await Promise.all(
      dashboardConfig.charts.map(async (chart) => {
        const { slot_number, equipment_id, parameter_key, site_id } = chart;

        if (chart.chart_type === 'live_position') {
          return;
        }
        if (!equipment_id) {
          return;
        }

        try {
          const chartType = getChartType(equipment_id);
          
          // 如果找不到機具數據，嘗試從 parameter_key 推斷類型
          // 或者對於某些已知的機具類型（如舊配置），嘗試直接調用 API
          if (!chartType) {
            console.warn(`⚠️ 找不到機具 ${equipment_id} 在 machinesData 中，嘗試直接調用 API`);
            
            // 嘗試直接調用 InfraredCamera API（function_15 可能是舊配置）
            try {
              const infraredResponse = await InfraredCamera.GetChartData(equipment_id, { range: '72h' });
              if (infraredResponse.code === 200 && infraredResponse.data) {
                console.log('✅ [Infrared Fallback] 成功從 API 載入數據');
                data[slot_number] = {
                  type: 'infrared',
                  data: infraredResponse.data,
                  functionData: {},
                };
                return;
              }
            } catch (err) {
              console.log('❌ [Infrared Fallback] API 調用失敗:', err.message);
            }
            
            // 如果所有嘗試都失敗，才顯示錯誤
            console.error(`❌ 無法載入機具 ${equipment_id} 的數據`);
            data[slot_number] = {
              type: 'error',
              message: '找不到機具數據，請重新設定此圖表'
            };
            return;
          }
          
          // Function 14 (氣體監測) - 獲取所有傳感器數據
          if (chartType?.type === 'gas') {
            const endDate = new Date();
            const startDate = new Date(endDate.getTime() - 24 * 60 * 60 * 1000); // 最近 24 小時
            
            const formatDate = (date) => {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const hours = String(date.getHours()).padStart(2, '0');
              const minutes = String(date.getMinutes()).padStart(2, '0');
              const seconds = String(date.getSeconds()).padStart(2, '0');
              return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
            };

            const response = await Gas.getChartDataByDateRange(
              equipment_id,
              formatDate(startDate),
              formatDate(endDate)
            );

            if (response.code === 200 && response.data) {
              // 處理數據：轉換時間格式
              const processedData = response.data.map(row => ({
                time: new Date(row.time).getTime(),
                a_val: row.a_val,
                b_val: row.b_val,
                c_val: row.c_val,
                d_val: row.d_val,
                e_val: row.e_val,
                f_val: row.f_val,
                g_val: row.g_val,
                h_val: row.h_val,
                heat_index: row.heat_index,
              }));
              
              data[slot_number] = {
                type: 'gas',
                data: processedData,
                sensors: chartType.functionData.sensors || {},
              };
            }
          }
          // Function 7/10 (油壓監測) - 獲取 4 條折線數據
          else if (chartType?.type === 'oil') {
            const response = await OilMonitor.GetChartData(equipment_id, 14); // 最近 14 天
            
            if (response.code === 200 && response.data) {
              const functionData = chartType.functionData;
              const A = functionData?.coefficients_a || [1, 1, 1, 1];
              const B = functionData?.coefficients_b || [0, 0, 0, 0];
              
              // 處理數據：應用校正公式
              const processedData = response.data.map(row => {
                const time = new Date(row.time);
                return {
                  time: time.getTime(),
                  voltage: parseFloat((row.voltage * A[0] + B[0]).toFixed(2)),
                  Rpressure: parseFloat((row.Rpressure * A[1] + B[1]).toFixed(2)),
                  Spressure: parseFloat((row.Spressure * A[2] + B[2]).toFixed(2)),
                  Tpressure: parseFloat((row.Tpressure * A[3] + B[3]).toFixed(2)),
                };
              });
                            
              // 獲取油壓上下限和補點設置
              let boundsData = functionData?.bounds || { upperBound: 100, lowerBound: 0 };
              let makeUpValue = 0;
              
              try {
                const boundsRes = await OilMonitor.GetBounds(equipment_id);
                if (boundsRes.code === 200 && boundsRes.data.length > 0) {
                  const bounds = boundsRes.data[0];
                  boundsData = {
                    upperBound: bounds.upper_bound,
                    lowerBound: bounds.lower_bound,
                    tolerance: bounds.tolerance,
                  };
                }
                
                const makeUpRes = await OilMonitor.GetMakeUp(equipment_id);
                if (makeUpRes.code === 200) {
                  makeUpValue = makeUpRes.data;
                }
              } catch (err) {
                console.error('獲取油壓配置失敗:', err);
              }
              
              data[slot_number] = {
                type: 'oil',
                data: processedData,
                bounds: boundsData,
                makeUp: makeUpValue,
                equipmentId: equipment_id,
              };
              
              // 初始化補點開關狀態
              setOilChartMakeUp(prev => ({
                ...prev,
                [slot_number]: makeUpValue === 1
              }));
            }
          }
          // Function 11 (水情監測) - 方案3：先查14天 water_level，根據時間範圍查其他參數
          else if (chartType?.type === 'water') {
            console.log('🌊 [Water] 開始查詢水情監測數據:', equipment_id);
            
            try {
              // 步驟1：先查詢 water_level 最近500筆資料
              const waterLevelData = await Dashboard.GetMachineHistory(equipment_id, 'water_level', 500);
              
              if (waterLevelData.length === 0) {
                console.warn('🌊 [Water] 沒有 water_level 資料');
                data[slot_number] = {
                  type: 'water',
                  data: [],
                  functionData: chartType.functionData,
                };
              } else {
                // 計算14天前的時間戳
                const now = new Date();
                const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
                
                // 過濾出14天內的資料
                let recentWaterLevel = waterLevelData.filter(item => {
                  const itemDate = new Date(item.time);
                  return itemDate >= fourteenDaysAgo;
                });
                
                console.log('🌊 [Water] water_level 原始資料:', waterLevelData.length, '筆');
                console.log('🌊 [Water] water_level 14天內資料:', recentWaterLevel.length, '筆');
                
                // 如果14天內沒有資料，就使用全部500筆
                if (recentWaterLevel.length === 0) {
                  console.warn('🌊 [Water] 14天內沒有資料，改用全部', waterLevelData.length, '筆');
                  recentWaterLevel = waterLevelData;
                }
                
                // 步驟2：獲取時間範圍
                const startTime = new Date(recentWaterLevel[0].time);
                const endTime = new Date(recentWaterLevel[recentWaterLevel.length - 1].time);
                
                // 格式化為後端需要的格式 (YYYY-MM-DD HH:MM:SS)
                const formatDateTime = (date) => {
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const hours = String(date.getHours()).padStart(2, '0');
                  const minutes = String(date.getMinutes()).padStart(2, '0');
                  const seconds = String(date.getSeconds()).padStart(2, '0');
                  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                };
                
                const startDateStr = formatDateTime(startTime);
                const endDateStr = formatDateTime(endTime);
                
                console.log('🌊 [Water] 時間範圍:', startDateStr, '到', endDateStr);
                
                // 步驟3：使用相同時間範圍查詢其他兩個參數
                const parameters = ['water_level', 'average_velocity', 'discharge'];
                const dataPromises = parameters.map(param => 
                  Dashboard.GetMachineHistory(equipment_id, param, 500, startDateStr, endDateStr)
                );
                
                const results = await Promise.all(dataPromises);
                console.log('🌊 [Water] 查詢結果:', {
                  water_level: results[0]?.length || 0,
                  average_velocity: results[1]?.length || 0,
                  discharge: results[2]?.length || 0,
                });
                
                // 合併數據
                const timeMap = new Map();
                results.forEach((paramData, index) => {
                  const paramKey = parameters[index];
                  console.log(`🌊 [Water] 處理 ${paramKey}:`, paramData?.length || 0, '筆資料');
                  paramData.forEach(item => {
                    const time = new Date(item.time).getTime();
                    if (!timeMap.has(time)) {
                      timeMap.set(time, { time });
                    }
                    timeMap.get(time)[paramKey] = item.value;
                  });
                });
                
                // 轉換為數組並排序，只保留三個參數都有值的資料點
                const processedData = Array.from(timeMap.values())
                  .filter(item => 
                    item.water_level !== undefined && 
                    item.average_velocity !== undefined && 
                    item.discharge !== undefined
                  )
                  .sort((a, b) => a.time - b.time);
                
                console.log('🌊 [Water] 合併後數據:', processedData.length, '筆', processedData[0]);
                
                data[slot_number] = {
                  type: 'water',
                  data: processedData,
                  functionData: chartType.functionData,
                };
              }
            } catch (error) {
              console.error('🌊 [Water] 查詢失敗:', error);
              data[slot_number] = {
                type: 'water',
                data: [],
                functionData: chartType.functionData,
              };
            }
          }
          // Function 15 (紅外線熱像監測) - 使用 InfraredCamera API
          else if (chartType?.type === 'infrared') {
            console.log('🔥 [Infrared] 開始查詢紅外線熱像數據:', equipment_id);
            
            try {
              // 使用 InfraredCamera API 取得圖表數據（72h 預設值）
              const response = await InfraredCamera.GetChartData(equipment_id, { range: '72h' });
              
              if (response.code === 200 && response.data) {
                console.log('🔥 [Infrared] 成功載入數據:', response.data.history?.length || 0, '筆');
                
                data[slot_number] = {
                  type: 'infrared',
                  data: response.data,
                  functionData: chartType.functionData,
                };
              } else {
                console.warn('🔥 [Infrared] API 返回無數據');
                data[slot_number] = {
                  type: 'infrared',
                  data: { history: [], temp_bands: [] },
                  functionData: chartType.functionData,
                };
              }
            } catch (error) {
              console.error('🔥 [Infrared] 查詢失敗:', error);
              data[slot_number] = {
                type: 'infrared',
                data: { history: [], temp_bands: [] },
                functionData: chartType.functionData,
              };
            }
          }
          // 其他類型 - 使用原有的單參數 API
          else if (parameter_key) {
            const historyData = await Dashboard.GetMachineHistory(
              equipment_id,
              parameter_key,
              60
            );
            
            data[slot_number] = {
              type: 'simple',
              data: historyData,
            };
          }
        } catch (error) {
          console.error(`獲取圖表 ${slot_number} 的歷史數據失敗:`, error);
        }
      })
    );

    setChartData(data);
    
    // 更新圖表機具的在線狀態
    // 如果圖表成功載入數據（非 error 類型且有數據），則標記為在線
    const chartOnlineStatus = {};
    Object.entries(data).forEach(([slotNumber, chartItem]) => {
      const chart = dashboardConfig.charts.find(c => c.slot_number === parseInt(slotNumber));
      if (chart?.equipment_id) {
        const hasValidData = chartItem.type !== 'error' && (
          (chartItem.type === 'infrared' && chartItem.data?.history?.length > 0) ||
          (chartItem.type === 'gas' && chartItem.data?.length > 0) ||
          (chartItem.type === 'oil' && chartItem.data?.length > 0) ||
          (chartItem.type === 'water' && chartItem.data?.length > 0) ||
          (chartItem.type === 'simple' && chartItem.data?.length > 0)
        );
        chartOnlineStatus[chart.equipment_id] = hasValidData;
        if (hasValidData) {
          console.log(`✅ [Online] 機具 ${chart.equipment_id} 標記為在線（有圖表數據）`);
        }
      }
    });
    
    // 合併圖表的在線狀態到現有狀態
    setOnlineStatus(prev => ({
      ...prev,
      ...chartOnlineStatus
    }));
  }, [dashboardConfig, machinesData]);

  // 當機具資訊載入完成後，開始刷新數據
  useEffect(() => {
    if (Object.keys(machinesData).length > 0) {
      refreshParamValues();
      refreshChartData();

      // 設置定時器，每分鐘刷新一次
      const timer = setInterval(() => {
        refreshParamValues();
        refreshChartData();
      }, 60000);

      return () => clearInterval(timer);
    }
  }, [machinesData, refreshParamValues, refreshChartData]);

  const handleEdit = () => {
    history.push(`/dashboard/config/${companyId}`);
  };

  const handleRefresh = () => {
    message.info("正在刷新數據...");
    loadDashboardConfig(); // 重新載入所有資料
  };

  // 處理刪除儀表板
  const handleDelete = async () => {
    if (!companyId) {
      message.error("無法刪除儀表板：找不到公司ID");
      return;
    }

    setDeleteLoading(true);
    try {
      await Dashboard.Delete(companyId);
      message.success("儀表板已成功刪除");
      history.push("/control");
    } catch (error) {
      console.error("刪除儀表板失敗:", error);
      message.error("刪除儀表板失敗: " + (error.message || "未知錯誤"));
    } finally {
      setDeleteLoading(false);
    }
  };

  const sbTheme = dashboardConfig?.theme === 'space_blue';
  const SB = {
    card:    'rgba(13,27,42,0)',
    backdrop:'blur(3px)',
    border:  '2px solid rgba(135,206,235,0.3)',
    glow:    'rgba(0,0,0,0.37) 0px 8px 32px 0px',
    text:    '#ffffff',
    dim:     'rgb(135,206,235)',
    value:   '#ffffff',
    btnBackgroundColor: '#ccc',
    btnHover: '#999',
    btnBorder: '1px solid rgba(255, 255, 255, 1)',
    btnBorderHover: '1px solid rgba(100, 100, 100, 1)',

  };

  // 渲染參數卡片
  const renderParameters = () => {
    if (!dashboardConfig?.parameters) return null;

    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {dashboardConfig.parameters.map((param) => {
          const isOnline = onlineStatus[param.equipment_id];
          const value = paramValues[param.slot_number];

          return (
            <Col key={param.slot_number} xs={12} sm={12} md={6} lg={6}>
              <Card
                style={{
                  textAlign: "left",
                  backgroundColor: sbTheme ? SB.card : "#ffffff",
                  backdropFilter: sbTheme ? SB.backdrop : undefined,
                  borderRadius: "12px",
                  border: sbTheme ? SB.border : "1px solid #e8e8e8",
                  boxShadow: sbTheme ? SB.glow : undefined,
                }}
                styles={{ body: { padding: "20px", backgroundColor: sbTheme ? SB.card : undefined } }}
              >
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8
                }}>
                  <div style={{
                    margin: 0,
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    fontSize: '1rem',
                    lineHeight: 1.5,
                    color: sbTheme ? SB.dim : "#666",
                    fontWeight: 500,
                  }}>
                    {param.display_name}
                  </div>
                  {param.equipment_id && (
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: isOnline ? "#52c41a" : "#ff4d4f",
                      }}
                      title={isOnline ? "設備在線" : "設備離線"}
                    />
                  )}
                </div>
                <div
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 500,
                    color: sbTheme ? (value != null ? SB.value : 'rgba(255,255,255,0.2)') : (value != null ? "#262626" : "#d9d9d9"),
                    lineHeight: 1.2,
                  }}
                >
                  {value != null ? value : "--"}
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };

  // 渲染圖表和影片
  const renderChartsAndVideos = () => {
    const charts = dashboardConfig?.charts || [];
    const videos = dashboardConfig?.videos || [];

    if (charts.length === 0 && videos.length === 0) return null;

    return (
      <Row gutter={[16, 16]}>
        {charts.map((chart) => {
          // 即時位置追蹤：獨立渲染，不走圖表資料流程
          if (chart.chart_type === 'live_position') {
            // The tracker draws inside this card and appends its 場外 status to the title.
            const frame = (title, body) => (
                <Card
                  title={title}
                  style={{ height: "400px", backgroundColor: sbTheme ? SB.card : "#ffffff", backdropFilter: sbTheme ? SB.backdrop : undefined, borderRadius: "12px", border: sbTheme ? SB.border : undefined, boxShadow: sbTheme ? SB.glow : undefined }}
                  styles={{ header: sbTheme ? { background: SB.card, borderBottom: SB.border, color: SB.text } : {}, body: { height: "calc(100% - 57px)", padding: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: sbTheme ? SB.card : undefined } }}
                >
                  {body}
                </Card>
            );
            return (
              <Col key={`chart-${chart.slot_number}`} xs={24} sm={24} md={12} lg={12}>
                  {chart.equipment_id ? (
                    <LivePositionTracker
                      machineID={String(chart.equipment_id)}
                      showCard={false}
                      renderFrame={(withStatus, body) => frame(withStatus(chart.title), body)}
                    />
                  ) : frame(chart.title, (
                    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#ccc" }}>
                      未設定定位機具
                    </div>
                  ))}
              </Col>
            );
          }

          const chartDataItem = chartData[chart.slot_number];
          const isOnline = onlineStatus[chart.equipment_id];
          const chartType = getChartType(chart.equipment_id);
          
          // 記錄調試信息
          if (chartDataItem) {
            const dataLength = chartDataItem.type === 'infrared' 
              ? chartDataItem.data?.history?.length 
              : chartDataItem.data?.length;
            console.log('📊 [Render] slot:', chart.slot_number, 'type:', chartDataItem.type, 'data length:', dataLength);
          }
          
          const hasData = chartDataItem && 
            ((chartDataItem.type === 'simple' && chartDataItem.data?.length > 0) ||
             (chartDataItem.type === 'gas' && chartDataItem.data?.length > 0) ||
             (chartDataItem.type === 'oil' && chartDataItem.data?.length > 0) ||
             (chartDataItem.type === 'water' && chartDataItem.data?.length > 0) ||
             (chartDataItem.type === 'infrared' && chartDataItem.data?.history?.length > 0));
          
          // 檢查是否是錯誤狀態
          const isError = chartDataItem?.type === 'error';

          return (
            <Col key={`chart-${chart.slot_number}`} xs={24} sm={24} md={12} lg={12}>
              <Card
                title={
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center" 
                  }}>
                    <span>{chart.title}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {/* 根據不同 function 顯示不同按鈕 */}
                      {hasData && chartDataItem && (
                        <>
                          {/* 詳細圖表按鈕 - 所有類型都支援 */}
                          {sbTheme ? (
                            <span
                              onClick={() => openAdvancedChart(chart)}
                              onMouseEnter={() => setHoveredBtn(chart.slot_number)}
                              onMouseLeave={() => setHoveredBtn(null)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                padding: '2px 8px',
                                fontSize: 12,
                                cursor: 'pointer',
                                color: '#000000',
                                background: hoveredBtn === chart.slot_number ? SB.btnHover : SB.btnBackgroundColor,
                                border: hoveredBtn === chart.slot_number ? SB.btnBorderHover : SB.btnBorder,
                                borderRadius: 4,
                                transition: 'background 0.2s, border 0.2s',
                              }}
                            >
                              <LineChartOutlined />
                              詳細圖表
                            </span>
                          ) : (
                            <Button
                              type="link"
                              size="small"
                              icon={<LineChartOutlined />}
                              onClick={() => openAdvancedChart(chart)}
                              style={{ padding: '0 4px', fontSize: 12 }}
                            >
                              詳細圖表
                            </Button>
                          )}
                          
                          {/* 氣體圖表可以添加其他按鈕，例如：時間選擇 */}
                          {/* {chartDataItem.type === 'gas' && (
                            <Button type="link" size="small">時間設定</Button>
                          )} */}
                          
                          {/* 油壓圖表可以添加其他按鈕，例如：補點開關 */}
                          {/* {chartDataItem.type === 'oil' && (
                            <Switch size="small" />
                          )} */}
                        </>
                      )}
                      {/* 在線狀態指示 */}
                      {chart.equipment_id && (
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: isOnline ? "#52c41a" : "#ff4d4f",
                          }}
                          title={isOnline ? "設備在線" : "設備離線"}
                        />
                      )}
                    </div>
                  </div>
                }
                style={{
                  height: chartDataItem?.type === 'oil' ? "500px" : "400px",
                  backgroundColor: sbTheme ? SB.card : "#ffffff",
                  backdropFilter: sbTheme ? SB.backdrop : undefined,
                  borderRadius: "12px",
                  border: sbTheme ? SB.border : undefined,
                  boxShadow: sbTheme ? SB.glow : undefined,
                }}
                styles={{
                  header: sbTheme ? { background: SB.card, borderBottom: SB.border, color: SB.text } : {},
                  body: { height: "calc(100% - 57px)", padding: "12px 16px", display: "flex", flexDirection: "column", backgroundColor: sbTheme ? SB.card : undefined }
                }}
              >
                {isError ? (
                  <div
                    style={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ff4d4f",
                      flexDirection: "column",
                      gap: 8
                    }}
                  >
                    <div style={{ fontSize: 16, fontWeight: 'bold' }}>⚠️ 配置錯誤</div>
                    <div style={{ fontSize: 14 }}>{chartDataItem.message}</div>
                    <Button 
                      type="primary" 
                      size="small" 
                      onClick={() => history.push(`/dashboard/config/${companyId}`)}
                      style={{ marginTop: 8 }}
                    >
                      前往配置頁面
                    </Button>
                  </div>
                ) : !hasData ? (
                  <div
                    style={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ccc",
                      flexDirection: "column",
                    }}
                  >
                    <div style={{ fontSize: 14 }}>
                      {chart.equipment_id ? "尚無資料" : "未設定資料來源"}
                    </div>
                  </div>
                ) : (
                  <>
                  {/* Function 14 - 氣體監測多折線圖 */}
                  {chartDataItem.type === 'gas' && (
                    <div style={{ flex: 1, minHeight: 0 }}>
                      <GasChartComponent 
                        data={chartDataItem.data} 
                        sensors={chartDataItem.sensors}
                        showAdvancedButton={false}
                      />
                    </div>
                  )}
                  
                  {/* Function 7/10 - 油壓監測 4 折線圖 + 信息区 */}
                  {chartDataItem.type === 'oil' && (
                    <OilChartComponent 
                      data={chartDataItem.data} 
                      bounds={chartDataItem.bounds}
                      showAdvancedButton={false}
                    />
                  )}
                  
                  {/* Function 11 - 水情監測 3 折線圖 */}
                  {chartDataItem.type === 'water' && (
                    <div style={{ flex: 1, minHeight: 0 }}>
                      <WaterChartComponent 
                        data={chartDataItem.data}
                        showAdvancedButton={false}
                      />
                    </div>
                  )}
                  
                  {/* Function 15 - 紅外線熱像監測 雙圖表 */}
                  {chartDataItem.type === 'infrared' && (
                    <div style={{ flex: 1, minHeight: 0 }}>
                      <Function15ChartComponent 
                        data={chartDataItem.data}
                        showAdvancedButton={false}
                      />
                    </div>
                  )}
                  
                  {/* 簡單圖表 - 單折線 */}
                  {chartDataItem.type === 'simple' && (
                    <div style={{ flex: 1, minHeight: 0 }}>
                      <SimpleChartComponent 
                        data={chartDataItem.data}
                        title={chart.title}
                        showAdvancedButton={false}
                      />
                    </div>
                  )}
                  </>
                )}
              </Card>
            </Col>
          );
        })}

        {videos.map((video) => (
          <Col key={`video-${video.slot_number}`} xs={24} sm={24} md={12} lg={12}>
            <MediaCard mediaConfig={video} sbTheme={sbTheme} SB={SB} />
          </Col>
        ))}
      </Row>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
          }}
        >
          <Spin size="large">
            <div style={{ padding: 50, color: "#999" }}>載入儀表板中...</div>
          </Spin>
        </div>
      );
    }

    if (!dashboardConfig) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
          }}
        >
          <Empty description="尚未建立儀表板配置" image={Empty.PRESENTED_IMAGE_SIMPLE}>
            <Button type="primary" onClick={handleEdit}>
              立即建立儀表板
            </Button>
          </Empty>
        </div>
      );
    }

    return (
      <div style={{ marginTop: 40 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h2 style={{ margin: 0, fontWeight: 600 }}>{dashboardConfig.title}</h2>
          <div>
            <Button onClick={handleRefresh} icon={<ReloadOutlined />} style={{ marginRight: 8 }}>
              刷新數據
            </Button>
            {user && user.class == process.env.REACT_APP_CONTROL_CALSS_LV && (
              <>
                <Button type="primary" icon={<EditOutlined />} onClick={handleEdit} style={{ marginRight: 8 }}>
                  編輯儀表板
                </Button>
                <Popconfirm
                  title="確定要刪除此儀表板嗎？"
                  description="此操作無法撤銷，刪除後所有配置將會丟失。"
                  onConfirm={handleDelete}
                  okText="確定刪除"
                  cancelText="取消"
                  okButtonProps={{ danger: true, loading: deleteLoading }}
                >
                  <Button danger icon={<DeleteOutlined />}>
                    刪除儀表板
                  </Button>
                </Popconfirm>
              </>
            )}
          </div>
        </div>
        {sbTheme ? (
          <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', padding: '20px 24px' }}>
            <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${bgSpaceBlue})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(2,8,28,0.62)' }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              {renderParameters()}
              {renderChartsAndVideos()}
            </div>
          </div>
        ) : (
          <>
            {renderParameters()}
            {renderChartsAndVideos()}
          </>
        )}
      </div>
    );
  };

  // 渲染高級圖表組件
  const renderAdvancedChart = () => {
    if (!selectedChartConfig || !advancedChartVisible) {
      return null;
    }

    const { chartType, equipment_id, slot_number, title } = selectedChartConfig;
    const chartDataItem = chartData[slot_number];

    if (!chartDataItem) {
      return null;
    }

    // 缩放处理函数
    const handleZoom = () => {
      const { refAreaLeft, refAreaRight } = zoomState;
      if (!refAreaLeft || !refAreaRight || refAreaLeft === refAreaRight) {
        setZoomState(prev => ({ ...prev, refAreaLeft: '', refAreaRight: '' }));
        return;
      }

      // 确保 left < right
      const [left, right] = refAreaLeft <= refAreaRight 
        ? [refAreaLeft, refAreaRight] 
        : [refAreaRight, refAreaLeft];

      setZoomState({
        left,
        right,
        refAreaLeft: '',
        refAreaRight: '',
      });
    };

    const handleZoomOut = () => {
      setZoomState({
        left: 'dataMin',
        right: 'dataMax',
        refAreaLeft: '',
        refAreaRight: '',
      });
    };

    // 动态时间格式函数（根据缩放程度调整显示格式）
    const formatTime = (num) => {
      const { left, right } = zoomState;
      const defaultFormat = new Date(num).toLocaleDateString();
      
      // 如果是完整视图，显示日期
      if (left === 'dataMin' || right === 'dataMax') {
        return defaultFormat;
      }
      
      // 如果缩放后的时间范围 > 24小时，显示日期
      if (right - left > 24 * 60 * 60 * 1000) {
        return defaultFormat;
      }
      
      // 否则显示完整时间（日期 + 时分秒）
      return new Date(num).toLocaleString('zh-TW');
    };

    return (
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <span>{title} - 詳細圖表</span>
            {chartDataItem.type !== 'infrared' && (
              <button
                onClick={handleZoomOut}
                onMouseEnter={() => setResetZoomHovered(true)}
                onMouseLeave={() => setResetZoomHovered(false)}
                style={{
                  padding: '1px 7px',
                  fontSize: 14,
                  lineHeight: '22px',
                  cursor: 'pointer',
                  background: resetZoomHovered ? '#e6e6e6' : '#ffffff',
                  border: resetZoomHovered ? '1px solid #999' : '1px solid #d9d9d9',
                  borderRadius: 6,
                  transition: 'background 0.2s, border-color 0.2s',
                }}
              >
                重置縮放
              </button>
            )}
            {chartDataItem.type === 'water' && (
              <RangePicker
                showTime={{
                  format: "HH:mm",
                }}
                format="YYYY-MM-DD HH:mm"
                value={dateRange}
                onChange={handleDateRangeChange}
                placeholder={['開始時間', '結束時間']}
                style={{ width: 360 }}
              />
            )}
            {chartDataItem.type === 'infrared' && (
              <>
                {[
                  { key: '24h', label: '24h' },
                  { key: '72h', label: '72h' },
                  { key: '7d', label: '7d' },
                  { key: '30d', label: '30d' },
                ].map((opt) => (
                  <Button
                    key={opt.key}
                    size="small"
                    type={infraredChartRange === opt.key ? 'primary' : 'default'}
                    onClick={() => handleInfraredChangeRange(opt.key)}
                  >
                    {opt.label}
                  </Button>
                ))}
                <RangePicker
                  size="small"
                  showTime={{ format: "HH:00" }}
                  format="MM/DD HH:00"
                  value={infraredCustomRange}
                  onChange={handleInfraredCustomRange}
                  allowClear
                  placeholder={['開始時間', '結束時間']}
                />
              </>
            )}
          </div>
        }
        open={advancedChartVisible}
        onCancel={() => setAdvancedChartVisible(false)}
        width="90%"
        style={{ top: 20 }}
        footer={null}
        destroyOnClose
      >
        <div style={{ height: '70vh' }}>
          <ResponsiveContainer width="100%" height="100%">
            {/* Function 14 - 氣體監測 */}
            {chartDataItem.type === 'gas' && (
              <LineChart 
                data={chartDataItem.data}
                onMouseDown={(e) => e && setZoomState(prev => ({ ...prev, refAreaLeft: e.activeLabel }))}
                onMouseMove={(e) => zoomState.refAreaLeft && e && setZoomState(prev => ({ ...prev, refAreaRight: e.activeLabel }))}
                onMouseUp={handleZoom}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  allowDataOverflow
                  dataKey="time"
                  domain={[zoomState.left, zoomState.right]}
                  type="number"
                  tickFormatter={formatTime}
                  tickCount={20}
                />
                <YAxis />
                <Tooltip labelFormatter={(t) => new Date(t).toLocaleString("zh-TW")} />
                <Legend />
                {Object.entries(chartDataItem.sensors || {}).map(([key, sensor], index) => {
                  if (!sensor.name || sensor.name.trim() === '') return null;
                  const dataKeyMap = {
                    'a': 'a_val', 'b': 'b_val', 'c': 'c_val', 'd': 'd_val',
                    'e': 'e_val', 'f': 'f_val', 'g': 'g_val', 'h': 'h_val'
                  };
                  const dataKey = dataKeyMap[key];
                  const colors = ["#ff4d4f", "#1890ff", "#52c41a", "#722ed1", "#fa8c16", "#eb2f96", "#13c2c2", "#a0d911"];
                  const color = colors[index % colors.length];
                  return (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={dataKey}
                      name={sensor.name}
                      stroke={color}
                      dot={false}
                      animationDuration={300}
                    />
                  );
                })}
                {zoomState.refAreaLeft && zoomState.refAreaRight && (
                  <ReferenceArea
                    x1={zoomState.refAreaLeft}
                    x2={zoomState.refAreaRight}
                    strokeOpacity={0.3}
                    fill="#8884d8"
                    fillOpacity={0.3}
                  />
                )}
              </LineChart>
            )}

            {/* Function 7/10 - 油壓監測 */}
            {chartDataItem.type === 'oil' && (
              <LineChart 
                data={chartDataItem.data}
                onMouseDown={(e) => e && setZoomState(prev => ({ ...prev, refAreaLeft: e.activeLabel }))}
                onMouseMove={(e) => zoomState.refAreaLeft && e && setZoomState(prev => ({ ...prev, refAreaRight: e.activeLabel }))}
                onMouseUp={handleZoom}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                {chartDataItem.bounds && (
                  <>
                    <ReferenceLine y={chartDataItem.bounds.lowerBound} label="油壓下限" stroke="red" yAxisId="1" />
                    <ReferenceLine y={chartDataItem.bounds.upperBound} label="油壓上限" stroke="red" yAxisId="1" />
                  </>
                )}
                <XAxis
                  allowDataOverflow
                  dataKey="time"
                  domain={[zoomState.left, zoomState.right]}
                  type="number"
                  tickFormatter={formatTime}
                  tickCount={20}
                />
                <YAxis
                  allowDataOverflow
                  domain={[
                    chartDataItem.bounds ? chartDataItem.bounds.lowerBound - 0.5 : "auto",
                    chartDataItem.bounds ? chartDataItem.bounds.upperBound + 0.5 : "auto"
                  ]}
                  tickFormatter={(value) => value.toFixed(2)}
                  yAxisId="1"
                  unit="bar"
                />
                <YAxis
                  orientation="right"
                  allowDataOverflow
                  domain={[9, 13]}
                  yAxisId="2"
                  unit="V"
                />
                <Tooltip labelFormatter={(e) => new Date(e).toLocaleString()} />
                <Legend />
                <Line yAxisId="1" type="linear" dataKey="Rpressure" name="R相油壓" stroke="blue" unit="bar" dot={false} animationDuration={300} />
                <Line yAxisId="1" type="linear" dataKey="Spressure" name="S相油壓" stroke="green" unit="bar" dot={false} animationDuration={300} />
                <Line yAxisId="1" type="linear" dataKey="Tpressure" name="T相油壓" stroke="black" unit="bar" dot={false} animationDuration={300} />
                <Line yAxisId="2" type="linear" dataKey="voltage" name="電壓" stroke="grey" unit="V" dot={false} animationDuration={300} />
                {zoomState.refAreaLeft && zoomState.refAreaRight && (
                  <ReferenceArea
                    yAxisId="1"
                    x1={zoomState.refAreaLeft}
                    x2={zoomState.refAreaRight}
                    strokeOpacity={0.3}
                    fill="#8884d8"
                    fillOpacity={0.3}
                  />
                )}
              </LineChart>
            )}

            {/* 簡單圖表 */}
            {chartDataItem.type === 'simple' && (
              <LineChart 
                data={chartDataItem.data}
                onMouseDown={(e) => e && setZoomState(prev => ({ ...prev, refAreaLeft: e.activeLabel }))}
                onMouseMove={(e) => zoomState.refAreaLeft && e && setZoomState(prev => ({ ...prev, refAreaRight: e.activeLabel }))}
                onMouseUp={handleZoom}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  allowDataOverflow
                  dataKey="time"
                  domain={[zoomState.left, zoomState.right]}
                  type="number"
                  tickFormatter={formatTime}
                  tickCount={20}
                />
                <YAxis />
                <Tooltip labelFormatter={(t) => new Date(t).toLocaleString("zh-TW")} />
                <Legend />
                <Line type="monotone" dataKey="value" stroke="#1890ff" dot={false} name={title || "數值"} animationDuration={300} />
                {zoomState.refAreaLeft && zoomState.refAreaRight && (
                  <ReferenceArea
                    x1={zoomState.refAreaLeft}
                    x2={zoomState.refAreaRight}
                    strokeOpacity={0.3}
                    fill="#8884d8"
                    fillOpacity={0.3}
                  />
                )}
              </LineChart>
            )}

            {/* Function 11 - 水情監測 */}
            {chartDataItem.type === 'water' && (
              <LineChart 
                data={chartDataItem.data}
                onMouseDown={(e) => e && setZoomState(prev => ({ ...prev, refAreaLeft: e.activeLabel }))}
                onMouseMove={(e) => zoomState.refAreaLeft && e && setZoomState(prev => ({ ...prev, refAreaRight: e.activeLabel }))}
                onMouseUp={handleZoom}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  allowDataOverflow
                  dataKey="time"
                  domain={[zoomState.left, zoomState.right]}
                  type="number"
                  tickFormatter={formatTime}
                  tickCount={20}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(t) => new Date(t).toLocaleString("zh-TW")}
                  formatter={(value, name) => {
                    const nameMap = {
                      water_level: '水位',
                      average_velocity: '平均流速',
                      discharge: '流量'
                    };
                    return [value, nameMap[name] || name];
                  }}
                />
                <Legend 
                  formatter={(value) => {
                    const nameMap = {
                      water_level: '水位',
                      average_velocity: '平均流速',
                      discharge: '流量'
                    };
                    return nameMap[value] || value;
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="water_level" 
                  name="water_level"
                  stroke="#1890ff" 
                  dot={false} 
                  connectNulls
                  animationDuration={300}
                />
                <Line 
                  type="monotone" 
                  dataKey="average_velocity" 
                  name="average_velocity"
                  stroke="#52c41a" 
                  dot={false} 
                  connectNulls
                  animationDuration={300}
                />
                <Line 
                  type="monotone" 
                  dataKey="discharge" 
                  name="discharge"
                  stroke="#fa8c16" 
                  dot={false} 
                  connectNulls
                  animationDuration={300}
                />
                {zoomState.refAreaLeft && zoomState.refAreaRight && (
                  <ReferenceArea
                    x1={zoomState.refAreaLeft}
                    x2={zoomState.refAreaRight}
                    strokeOpacity={0.3}
                    fill="#8884d8"
                    fillOpacity={0.3}
                  />
                )}
              </LineChart>
            )}
            
            {/* Function 15 - 紅外線熱像監測 */}
            {chartDataItem.type === 'infrared' && (
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {infraredChartLoading ? (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%' 
                  }}>
                    <Spin tip="載入圖表資料中..." />
                  </div>
                ) : (
                  <Function15ChartComponent 
                    data={chartDataItem.data}
                    showAdvancedButton={false}
                  />
                )}
              </div>
            )}
          </ResponsiveContainer>
        </div>
      </Modal>
    );
  };

  return (
    <React.Fragment>
      <CustomHeader />
      <div className="container-fluid">
        <div className="page-container fade-in">
          <PageDirector />
          {renderContent()}
        </div>
      </div>
      
      {/* 高級圖表 Modal */}
      {renderAdvancedChart()}
    </React.Fragment>
  );
};

export default DashboardView;
